import { z } from "zod"
import { TimelineSchema } from "./timing"

// =============================================================================
// Effect Types
// =============================================================================

export const EffectTypeSchema = z.enum([
  "zoom",
  "camera_shake",
  "color_grade",
  "overlay_shape",
  "flash",
  "vignette",
])

// =============================================================================
// Effect Params (per-type)
// =============================================================================

export const ZoomParamsSchema = z.object({
  startScale: z.number().min(0.5).max(3).default(1.0),
  endScale: z.number().min(0.5).max(3).default(1.2),
  originX: z.number().min(0).max(1).default(0.5),
  originY: z.number().min(0).max(1).default(0.5),
})

export const CameraShakeParamsSchema = z.object({
  intensity: z.number().min(1).max(20).default(4),
  frequency: z.number().min(0.1).max(2).default(0.5),
  decay: z.boolean().default(false),
})

export const ColorGradeParamsSchema = z.object({
  brightness: z.number().min(0.5).max(2).default(1.0),
  contrast: z.number().min(0.5).max(2).default(1.0),
  saturate: z.number().min(0).max(3).default(1.0),
  temperature: z.number().min(-30).max(30).default(0),
})

export const OverlayShapeParamsSchema = z.object({
  shape: z.enum(["circle", "arrow", "rectangle"]).default("circle"),
  color: z.string().default("#FF0000"),
  xPct: z.number().min(0).max(1).default(0.5),
  yPct: z.number().min(0).max(1).default(0.3),
  size: z.number().min(20).max(400).default(80),
  strokeWidth: z.number().min(2).max(12).default(4),
  animation: z.enum(["pop_in", "bounce", "pulse", "none"]).default("pop_in"),
  rotation: z.number().min(-180).max(180).default(0),
})

export const FlashParamsSchema = z.object({
  color: z.string().default("#FFFFFF"),
  intensity: z.number().min(0.3).max(1).default(0.8),
  durationFrames: z.number().min(1).max(10).default(3),
})

export const VignetteParamsSchema = z.object({
  intensity: z.number().min(0.1).max(1).default(0.5),
  size: z.number().min(0.3).max(0.9).default(0.6),
})

export const EffectParamsSchema = z.union([
  ZoomParamsSchema,
  CameraShakeParamsSchema,
  ColorGradeParamsSchema,
  OverlayShapeParamsSchema,
])

// =============================================================================
// Effect Segment
// =============================================================================

export const EffectSegmentSchema = z.object({
  id: z.string(),
  effectType: EffectTypeSchema,
  timeline: TimelineSchema,
  params: z.record(z.string(), z.any()),
  enabled: z.boolean().default(true),
  sceneId: z.string().nullable().default(null),
})

// =============================================================================
// Type Exports
// =============================================================================

export type EffectType = z.infer<typeof EffectTypeSchema>
export type ZoomParams = z.infer<typeof ZoomParamsSchema>
export type CameraShakeParams = z.infer<typeof CameraShakeParamsSchema>
export type ColorGradeParams = z.infer<typeof ColorGradeParamsSchema>
export type OverlayShapeParams = z.infer<typeof OverlayShapeParamsSchema>
export type FlashParams = z.infer<typeof FlashParamsSchema>
export type VignetteParams = z.infer<typeof VignetteParamsSchema>
export type EffectSegment = z.infer<typeof EffectSegmentSchema>
